import { ROUTES } from '@cloud-ui/constants';
import { tryBrowserStorage } from '@cloud-ui/utils/browserStorage';

import { matchPath } from 'react-router-dom';

/**
 * Where the invite code from a share link waits for the next login. OauthCallback sends it with
 * the login, which redeems it for an account that login has just created. Anyone else is then asked on
 * the invite page, which takes it out of here and holds it for its tab. A logout forgets it.
 * Only "Use another account" carries a code over: the logout page then makes it pending again,
 * for that account's login. It is localStorage rather than sessionStorage because a
 * registration can finish in another tab, once the email is verified.
 */
const PENDING_INVITE_KEY = 'invite_code';

/**
 * Where a code waits in one tab: a code taken out of a share link's URL, until the visitor acts
 * on the landing page, and a code a signed-in user is being asked about, until they answer. It
 * is sessionStorage, which goes away with the tab, so a link that was only opened leaves nothing
 * behind for a later login to redeem. A code held for a signed-in user also ends with that
 * user's session: a logout forgets it, unless they chose another account on the invite card.
 */
const CAPTURED_INVITE_KEY = 'captured_invite_code';

/** Carries the code in a share link: in the fragment, or in the query of links issued earlier. */
const INVITE_PARAM = 'invite_code';

/** Set on the landing page's URL when the code could not be stored, so the page can say so. */
export const INVITE_ERROR_PARAM = 'invite_error';

/** Explicit account-mismatch recovery may preserve a different pending invitation. */
export const INVITE_ACCOUNT_REVIEW_PARAM = 'review_account';
export const INVITE_STORAGE_ERROR = 'storage';

const withoutOrigin = (url: URL) => `${url.pathname}${url.search}${url.hash}`;

/**
 * Separates the invite code of a share link from the rest of its URL. Returns null when the URL
 * is not the page share links point to, or carries no code.
 *
 * The code is read from the fragment first, then from the query, and removed from both, so the
 * returned URL never holds it. A fragment without a code is page state and is left as written.
 * Only the register page takes an invite, as before: a link to any other page must not be able
 * to leave a code behind for a later login.
 */
export function splitInviteFromUrl(href: string): { code: string; cleanedUrl: string } | null {
  const url = new URL(href);
  if (!matchPath(ROUTES.register, url.pathname)) {
    return null;
  }

  const fragment = new URLSearchParams(url.hash.slice(1));
  const code = fragment.get(INVITE_PARAM) || url.searchParams.get(INVITE_PARAM);
  if (!code) {
    return null;
  }

  // A delete re-serializes every other parameter too, so each part is only rewritten when it
  // holds a code.
  if (url.searchParams.has(INVITE_PARAM)) {
    url.searchParams.delete(INVITE_PARAM);
  }
  if (fragment.has(INVITE_PARAM)) {
    fragment.delete(INVITE_PARAM);
    url.hash = fragment.toString();
  }

  return { code, cleanedUrl: withoutOrigin(url) };
}

/**
 * Moves the invite code of a share link from the address bar into browser storage and reloads
 * the page without it. Returns true when it did: the page is about to reload, so the caller must
 * not render.
 *
 * Call it before the app mounts. Once mounted, the app's requests send the page URL, minus its
 * fragment, as their Referer, and the analytics SDKs record the whole URL as soon as they start.
 * Rewriting the URL first keeps the code from all of them. The reload replaces the document,
 * because the browser's navigation timing entry keeps the URL a document was requested with and
 * the SDKs report that entry too.
 *
 * The code is only captured here. It is not yet an invite that a login would redeem: that takes
 * the visitor choosing to continue on the landing page.
 */
export function captureInviteFromUrl(): boolean {
  const invite = splitInviteFromUrl(window.location.href);
  if (!invite) {
    return false;
  }

  let { cleanedUrl } = invite;
  const isAccountReview =
    new URL(cleanedUrl, window.location.href).searchParams.get(INVITE_ACCOUNT_REVIEW_PARAM) === '1';
  try {
    sessionStorage.setItem(CAPTURED_INVITE_KEY, invite.code);
    if (isAccountReview && sessionStorage.getItem(CAPTURED_INVITE_KEY) !== invite.code) {
      throw new Error('The invitation could not be stored for account review.');
    }
  } catch {
    // Without storage the invite is lost. The flag lets the landing page tell the visitor why.
    // A code from an earlier link must not stand in for it: continuing would then join that
    // link's organization.
    discardCapturedInvite();
    if (!isAccountReview || getPendingInvite() === invite.code) {
      clearPendingInvite();
    }
    const url = new URL(cleanedUrl, window.location.href);
    url.searchParams.set(INVITE_ERROR_PARAM, INVITE_STORAGE_ERROR);
    cleanedUrl = withoutOrigin(url);
  }

  window.history.replaceState(window.history.state, '', cleanedUrl);
  window.location.reload();
  return true;
}

/**
 * Captures the invite code of a share link opened in a tab that already shows the landing page.
 * Only the fragment changes then, so the browser keeps the document and the capture at startup
 * does not run again.
 *
 * The listeners run in the capture phase and stop the event, so the router and the analytics
 * SDKs, whenever they registered theirs, never handle a navigation whose URL holds a code.
 */
export function captureInviteOnNavigation(): void {
  const capture = (event: Event) => {
    if (captureInviteFromUrl()) {
      event.stopImmediatePropagation();
    }
  };
  window.addEventListener('popstate', capture, { capture: true });
  window.addEventListener('hashchange', capture, { capture: true });
}

/**
 * The code of a share link that this tab holds, if there is one. A signed-out visitor makes it
 * pending by choosing to register or log in. A signed-in visitor answers it where it is; it only
 * becomes pending for them when they choose another account on the invite card.
 */
export function getCapturedInvite(): string | null {
  return tryBrowserStorage(() => sessionStorage.getItem(CAPTURED_INVITE_KEY)) || null;
}

/**
 * Takes the invite a signed-in visitor is about to be asked about out of localStorage and holds
 * it for this tab only. Returns the code this tab now holds, if there is one.
 *
 * A code left pending is sent by the next login from this browser, whoever signs in. Once the
 * invite page asks a signed-in user about it, the answer is theirs to give on that page, so an
 * unanswered card must not leave the code behind for someone else's login. A code this tab took
 * out of a link comes first, and an older pending one is dropped.
 *
 * Explicit account-mismatch recovery can preserve a different pending invite from another flow.
 * It only claims the captured code and does not authorize acceptance.
 *
 * From then on the code ends with that user's session: a logout forgets it, unless they chose
 * another account on the card.
 *
 * A code the tab cannot store is returned all the same, and is no longer pending either. The
 * page that asked keeps it for as long as it is open. A reload then loses the invite, which is
 * the safe way for it to end.
 */
export function holdInviteForThisTab({ preserveOtherPending = false } = {}): string | null {
  const captured = getCapturedInvite();
  const pending = getPendingInvite();
  if (pending === null) {
    return captured;
  }

  if (captured === null) {
    tryBrowserStorage(() => sessionStorage.setItem(CAPTURED_INVITE_KEY, pending));
  }
  if (!preserveOtherPending || captured === null || captured === pending) {
    clearPendingInvite();
  }
  return captured ?? pending;
}

/** Whether a share link's code is waiting for the visitor to act on the landing page. */
export function hasCapturedInvite(): boolean {
  return getCapturedInvite() !== null;
}

/**
 * Makes the captured code the pending invite, which the next login carries. The landing page
 * calls this when a signed-out visitor chooses to register or log in, so opening a link is not
 * enough to have a later login join an organization. Returns false when the browser would not
 * store it. With nothing captured it does nothing and returns true.
 */
export function keepCapturedInvite(): boolean {
  const code = getCapturedInvite();
  if (!code) {
    return true;
  }

  try {
    localStorage.setItem(PENDING_INVITE_KEY, code);
  } catch {
    return false;
  }
  tryBrowserStorage(() => sessionStorage.removeItem(CAPTURED_INVITE_KEY));
  return true;
}

/** Forgets the captured code: the visitor is not offered the invite, or has answered it. */
export function discardCapturedInvite(): void {
  tryBrowserStorage(() => sessionStorage.removeItem(CAPTURED_INVITE_KEY));
}

/** The invite code waiting for the next login, if there is one. */
export function getPendingInvite(): string | null {
  return tryBrowserStorage(() => localStorage.getItem(PENDING_INVITE_KEY)) ?? null;
}

/**
 * Makes a code the pending invite, which the next login carries. The logout page calls this for
 * the invite that "Use another account" carried over.
 */
export function setPendingInvite(code: string): void {
  tryBrowserStorage(() => localStorage.setItem(PENDING_INVITE_KEY, code));
}

/** Forgets the pending invite, so that a later login from this browser does not redeem it. */
export function clearPendingInvite(): void {
  tryBrowserStorage(() => localStorage.removeItem(PENDING_INVITE_KEY));
}
